import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { MachineStatusValue, StatusMappingEntry } from "@/types/parameters.types";

const MACHINE_STATUSES: MachineStatusValue[] = ["RUNNING", "STOPPED", "IDLE", "NODATA"];

const STATUS_LABELS: Record<MachineStatusValue, string> = {
  RUNNING: "Rodando",
  STOPPED: "Parada",
  IDLE: "Ociosa",
  NODATA: "Sem dados",
};

interface StatusMappingEditorProps {
  entries: StatusMappingEntry[];
  onChange: (entries: StatusMappingEntry[]) => void;
}

export function StatusMappingEditor({ entries, onChange }: StatusMappingEditorProps) {
  const valueCounts = entries.reduce<Record<string, number>>((acc, e) => {
    const k = e.value.trim();
    if (k) acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});

  function addRow() {
    onChange([...entries, { value: "", status: "RUNNING" }]);
  }

  function removeRow(index: number) {
    onChange(entries.filter((_, i) => i !== index));
  }

  function updateRow(index: number, patch: Partial<StatusMappingEntry>) {
    onChange(entries.map((e, i) => (i === index ? { ...e, ...patch } : e)));
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Mapeamento valor → status</Label>
        <span className="text-[10px] text-muted-foreground">
          {entries.length === 0 ? "≥ 1 entrada obrigatória" : `${entries.length} entrada(s)`}
        </span>
      </div>
      {entries.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Adicione pelo menos um mapeamento (ex.: &quot;1&quot; → Rodando, &quot;0&quot; → Parada).
        </p>
      ) : null}
      <ul className="space-y-1.5">
        {entries.map((e, i) => {
          const trimmed = e.value.trim();
          const dup = trimmed && (valueCounts[trimmed] ?? 0) > 1;
          return (
            <li key={i} className="flex items-start gap-1.5">
              <div className="flex-1 space-y-0.5">
                <Input
                  value={e.value}
                  onChange={(ev) => updateRow(i, { value: ev.target.value })}
                  placeholder='ex.: "1", "rodando"'
                  maxLength={50}
                  aria-invalid={dup ? true : undefined}
                />
                {dup ? <p className="text-[10px] text-destructive">Valor duplicado.</p> : null}
              </div>
              <select
                value={e.status}
                onChange={(ev) => updateRow(i, { status: ev.target.value as MachineStatusValue })}
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                aria-label={`Status para ${e.value || "esta entrada"}`}
              >
                {MACHINE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Remover entrada"
                onClick={() => removeRow(i)}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" />
              </Button>
            </li>
          );
        })}
      </ul>
      <Button size="sm" variant="outline" className="w-full" onClick={addRow}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        Adicionar mapeamento
      </Button>
    </div>
  );
}
